import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Spinner, Alert, ListGroup, Row, Col } from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getAppraisal } from '../services/appraisalService';
import moment from 'moment';
import TopBar from '../../../components/TopBar';
const AppraisalView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [appraisal, setAppraisal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAppraisal = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await getAppraisal(id);
        
        if (data) {
          // Format the data for display
          const formattedAppraisal = {
            ...data,
            // Format dates
            review_period_start: data.review_period_start ? moment(data.review_period_start).format('MMMM D, YYYY') : 'N/A',
            review_period_end: data.review_period_end ? moment(data.review_period_end).format('MMMM D, YYYY') : 'N/A',
            // Format employee data
            employee: {
              id: data.employee_id,
              name: data.employee?.name || 'Unknown Employee',
              department: data.employee?.department?.name || 'N/A',
              designation: data.employee?.designation?.name || 'N/A'
            },
            // Format reviewer data
            reviewer: {
              id: data.reviewer_id,
              name: data.reviewer?.name || 'Not Assigned'
            },
            // Format timestamps
            created_at: data.created_at ? moment(data.created_at).format('MMMM D, YYYY h:mm A') : 'N/A',
            updated_at: data.updated_at ? moment(data.updated_at).format('MMMM D, YYYY h:mm A') : 'N/A',
            // Map snake_case to camelCase for consistency
            areasForImprovement: data.areas_for_improvement || data.areasForImprovement,
            // Format goals
            goals: data.goals?.map(goal => ({
              ...goal,
              target_date: goal.target_date ? moment(goal.target_date).format('MMMM D, YYYY') : 'N/A'
            })) || [],
            // Format skills
            skills: data.skills?.map(skill => ({
              id: skill.id,
              name: skill.skill_name || 'Unnamed Skill',
              rating: parseFloat(skill.rating) || 0,
              notes: skill.notes || '',
              category: skill.category || 'General'
            })) || []
          };
          
          setAppraisal(formattedAppraisal);
        }
      } catch (err) {
        console.error('Error loading appraisal:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        setError(err.response?.data?.message || 'Failed to load appraisal details');
      } finally {
        setLoading(false);
      }
    };

    fetchAppraisal();
  }, [id]);

  const getStatusVariant = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'primary';
      case 'Draft':
        return 'secondary';
      default:
        return 'light';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'primary';
      case 'Draft':
        return 'secondary';
      case 'Not Started':
        return 'danger';
      default:
        return 'light';
    }
  };

  // Helper function to get color based on rating
  const getRatingColor = (rating, opacity = 1) => {
    const value = parseFloat(rating);
    if (value >= 4) return `rgba(40, 167, 69, ${opacity})`;  // Green for high ratings
    if (value >= 3) return `rgba(255, 193, 7, ${opacity})`;   // Yellow for medium-high
    if (value >= 2) return `rgba(253, 126, 20, ${opacity})`;  // Orange for medium-low
    return `rgba(220, 53, 69, ${opacity})`;                  // Red for low ratings
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <h4>Error Loading Appraisal</h4>
        <p>{error}</p>
        <Button variant="primary" onClick={() => window.location.reload()} className="mt-2">
          Try Again
        </Button>
      </Alert>
    );
  }

  if (!appraisal) {
    return <Alert variant="warning">No appraisal data found</Alert>;
  }

  return (
    <>
     <TopBar />
    <div className="p-3">
     

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Appraisal Details</h4>
          <div>
            {/* <Badge bg={getStatusBadge(appraisal.status)} className="text-uppercase me-2">
              {appraisal.status}
            </Badge> */}
            <Button 
              variant="outline-primary" 
              size="sm" 
              as={Link} 
              to={`/app/appraisals/edit/${id}`}
              className="ms-2"
            >
              Edit Appraisal
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Employee:</strong> {appraisal.employee?.name || 'N/A'}</p>
              <p><strong>Department:</strong> {appraisal.employee?.department || 'N/A'}</p>
              <p><strong>Designation:</strong> {appraisal.employee?.designation || 'N/A'}</p>
            </Col>
            <Col md={6}>
              <p><strong>Review Period:</strong> {appraisal.review_period_start} - {appraisal.review_period_end}</p>
              <p><strong>Reviewer:</strong> {appraisal.reviewer?.name || 'Not Assigned'}</p>
              <p className="d-flex align-items-center">
                <strong className="me-2">Overall Rating:</strong>
                <span 
                  className="badge rounded-pill d-inline-flex align-items-center justify-content-center" 
                  style={{
                    backgroundColor: getRatingColor(appraisal.overall_rating || 0, 0.8),
                    color: 'white',
                    minWidth: '60px',
                    height: '24px',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  {appraisal.overall_rating ? `${appraisal.overall_rating}/5` : 'N/A'}
                </span>
              </p>
            </Col>
          </Row>
          {/* Increment Salary Details */}
          <Row className="mt-3">
            <Col md={4}>
              <p><strong>Current Salary:</strong> {appraisal.current_salary !== undefined ? `₹${parseFloat(appraisal.current_salary).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}</p>
            </Col>
            <Col md={4}>
              <p><strong>Increment:</strong> {appraisal.increment_percentage !== undefined ? `${appraisal.increment_percentage}%` : 'N/A'}</p>
            </Col>
            <Col md={4}>
              <p><strong>Updated Salary:</strong> {appraisal.updated_salary !== undefined ? `₹${parseFloat(appraisal.updated_salary).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}</p>
            </Col>
          </Row>
          
          {(appraisal.strengths || appraisal.areas_for_improvement || appraisal.comments) && (
            <Row className="mt-3">
              <Col md={12}>
                <h5>Evaluation Summary</h5>
                {appraisal.strengths && (
                  <div className="mb-3">
                    <h6>Strengths</h6>
                    <p className="text-muted">{appraisal.strengths}</p>
                  </div>
                )}
                {appraisal.areas_for_improvement && (
                  <div className="mb-3">
                    <h6>Areas for Improvement</h6>
                    <p className="text-muted">{appraisal.areas_for_improvement}</p>
                  </div>
                )}
                {appraisal.comments && (
                  <div>
                    <h6>Additional Comments</h6>
                    <p className="text-muted">{appraisal.comments}</p>
                  </div>
                )}
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>Skills Assessment</Card.Header>
        <Card.Body>
          {appraisal.skills?.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Skill</th>
                    <th style={{ width: '150px' }}>Rating</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {appraisal.skills.map((skill, index) => (
                    <tr key={skill.id || index}>
                      <td>{skill.name}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="me-2">
                            {skill.rating.toFixed(1)}
                          </div>
                          <div className="progress flex-grow-1" style={{ height: '8px' }}>
                            <div 
                              className="progress-bar bg-info" 
                              role="progressbar" 
                              style={{ width: `${(skill.rating / 5) * 100}%` }}
                              aria-valuenow={skill.rating}
                              aria-valuemin="0"
                              aria-valuemax="5"
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="text-muted">
                        <small>{skill.notes || 'No notes provided'}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-muted text-center py-3">No skills assessed for this appraisal.</div>
          )}
        </Card.Body>
      </Card>

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Strengths</Card.Header>
            <Card.Body>
              {appraisal.strengths ? (
                <div className="p-3 bg-light rounded">
                  {appraisal.strengths.split('\n').map((paragraph, i) => (
                    <p key={i} className="mb-0">{paragraph}</p>
                  ))}
                </div>
              ) : (
                <div className="text-muted">No strengths recorded.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Areas for Improvement</Card.Header>
            <Card.Body>
              {appraisal.areasForImprovement ? (
                <div className="p-3 bg-light rounded">
                  {appraisal.areasForImprovement.split('\n').map((paragraph, i) => (
                    <p key={i} className="mb-0">{paragraph}</p>
                  ))}
                </div>
              ) : (
                <div className="text-muted">No areas for improvement recorded.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {appraisal.comments && (
        <Card className="mb-4">
          <Card.Header>Additional Comments</Card.Header>
          <Card.Body>
            <div className="p-3 bg-light rounded">
              {appraisal.comments.split('\n').map((paragraph, i) => (
                <p key={i} className="mb-0">{paragraph}</p>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      <div className="d-flex justify-content-between align-items-center mt-4 text-muted small">
        <div>
          Created: {moment(appraisal.createdAt).format('MMM D, YYYY h:mm A')}
        </div>
        <div>
          Last updated: {moment(appraisal.updatedAt).format('MMM D, YYYY h:mm A')}
        </div>
      </div>
    </div>
    </>
  );
};

export default AppraisalView;
